import styles from "./styles.scss";
import { forwardRef, PropsWithChildren, useMemo } from "react";
import clsx from "clsx";
import { CheckMarkSmall, Indeterminate } from "@/assets/icons";
import { CheckBoxVariant } from "./constants";
import { CheckboxProps } from "./types";

export const Checkbox = forwardRef<
  HTMLInputElement,
  PropsWithChildren<CheckboxProps>
>(
  (
    {
      children,
      className,
      variant = CheckBoxVariant.Regular,
      disabled,
      ...props
    },
    ref,
  ) => {
    const hasChildren = Boolean(children) || children === 0;
    const dataProps = disabled ? { ["data-disabled"]: true } : undefined;

    const {
      label: labelStyle,
      decoration: decorationStyle,
      coloredBorder: coloredBorderStyles,
    } = useMemo(() => {
      const isLargeVariant = variant === CheckBoxVariant.Large;
      const hasColoredBorder = variant === CheckBoxVariant.WithColoredBorder;
      let labelStyles;

      if (isLargeVariant) {
        labelStyles = hasChildren ? styles.labelLarge : styles.labelLargeEmpty;
      } else {
        labelStyles = hasChildren
          ? styles.labelRegular
          : styles.labelRegularEmpty;
      }

      return {
        label: labelStyles,
        decoration: isLargeVariant
          ? styles.checkboxDecorationLarge
          : styles.checkboxDecorationRegular,
        coloredBorder:
          hasColoredBorder && styles.checkboxDecorationWithColoredBorder,
      };
    }, [hasChildren, variant]);

    return (
      <label
        className={clsx(labelStyle, className, styles.checkboxWrap)}
        {...dataProps}
      >
        <input
          className={styles.hiddenInput}
          ref={ref}
          type="checkbox"
          disabled={disabled}
          {...props}
        />
        <span
          className={clsx(decorationStyle, coloredBorderStyles)}
          {...dataProps}
          role="presentation"
        >
          <Indeterminate data-intermediate />
          <CheckMarkSmall />
        </span>
        {children}
      </label>
    );
  },
);
